import React, { useEffect, useState } from 'react';
import axios from 'axios';
const API_BASE = (window.__APP_CONFIG__ && window.__APP_CONFIG__.API_URL) || process.env.REACT_APP_API_URL || '';
if (API_BASE && !axios.defaults.baseURL) {
  axios.defaults.baseURL = API_BASE;
}

const CourseList = () => {
  // Helper for admin confirmation and password
  const confirmAdmin = (actionLabel = 'this action') => {
    const ok = window.confirm(`Are you sure you want to ${actionLabel}?`);
    if (!ok) return false;
    const pw = window.prompt('Enter admin password to continue');
    if (pw !== 'admin') { alert('Unauthorized'); return false; }
    return true;
  };
  const [courses, setCourses] = useState([]);
  const [editCourseId, setEditCourseId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchCourses();
  }, []);

  // CSV export helpers
  const csvEscape = (val) => {
    const s = String(val ?? '');
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };

  const handleExportCSV = () => {
    try {
      const headers = [
        'Course ID',
        'Name',
        'Fee Type',
        'Admission Fee',
        'Monthly Fee',
        'Total Fee (One-time)',
        'Teacher',
        'Organization',
        'Mode',
        'Status',
        'Created At',
        'Updated At'
      ];

      const rows = courses.map(c => [
        c._id || '',
        c.name || '',
        c.feeType || '',
        c.admissionFee ?? '',
        c.monthlyFee ?? '',
        c.onetimeFee ?? '',
        c.teacher || '',
        c.organization || '',
        c.mode || '',
        c.status || '',
        c.createdAt ? new Date(c.createdAt).toISOString() : '',
        c.updatedAt ? new Date(c.updatedAt).toISOString() : ''
      ]);

      const csv = [headers, ...rows]
        .map(r => r.map(csvEscape).join(','))
        .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0,10);
      a.href = url;
      a.download = `courses_export_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('❌ CSV export failed:', e);
      alert('CSV export failed. Check console for details.');
    }
  };

  const fetchCourses = () => {
    axios.get('/api/courses')
      .then(res => setCourses(res.data))
      .catch(err => console.error("❌ Error fetching courses:", err));
  };

  const handleEditClick = (course) => {
    setEditCourseId(course._id);
    setEditForm({ ...course });
  };

  const handleCancelClick = () => {
    setEditCourseId(null);
    setEditForm({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (id) => {
    if (!confirmAdmin('save changes to this course')) return;
    try {
      await axios.put(`/api/courses/${id}`, editForm);
      setEditCourseId(null);
      fetchCourses();
    } catch (error) {
      console.error("❌ Error updating course:", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    const pw = window.prompt('Enter admin password to delete');
    if (pw !== 'admin') { alert('Unauthorized'); return; }
    try {
      await axios.delete(`/api/courses/${id}`);
      fetchCourses();
    } catch (error) {
      console.error("❌ Error deleting course:", error);
    }
  };

  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '₹-';
    return `₹${Number(value).toLocaleString()}`;
  };

  const sortedCourses = React.useMemo(() => {
    const arr = [...courses];
    arr.sort((a, b) => {
      const aActive = (a?.status || '').toLowerCase() === 'active';
      const bActive = (b?.status || '').toLowerCase() === 'active';
      if (aActive !== bActive) return aActive ? -1 : 1; // active first
      // then by name ASC
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
    return arr;
  }, [courses]);

  return (
    <div className="max-w-6xl mx-auto bg-white p-6 shadow rounded mt-10">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">All Courses</h2><a href="/mentors">Mentor, Timetable &amp; Welcome Setup</a>
        <button
          type="button"
          onClick={handleExportCSV}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded shadow"
        >
          Export CSV
        </button>
      </div>
      <table className="min-w-full border-collapse border border-gray-300 text-sm">
        <thead className="bg-gray-200">
          <tr>
            <th className="border px-2 py-1">Name</th>
            <th className="border px-2 py-1">Type</th>
            <th className="border px-2 py-1">Admission Fee</th>
            <th className="border px-2 py-1">Monthly Fee</th>
            <th className="border px-2 py-1">Total Fee (One-time)</th>
            <th className="border px-2 py-1">Teacher</th>
            <th className="border px-2 py-1">Organization</th>
            <th className="border px-2 py-1">Mode</th>
            <th className="border px-2 py-1">Status</th>
            <th className="border px-2 py-1">Action</th>
          </tr>
        </thead>
        <tbody>
          {courses.length === 0 && (
            <tr>
              <td colSpan="10" className="text-center py-4">No courses found.</td>
            </tr>
          )}

          {sortedCourses.map((course) => (
            <tr key={course._id} className="hover:bg-gray-50">
              {editCourseId === course._id ? (
                <>
                  <td className="border px-2 py-1">
                    <input
                      name="name"
                      value={editForm.name}
                      onChange={handleChange}
                      className="border p-1 w-full"
                    />
                  </td>
                  <td className="border px-2 py-1">
                    <select
                      name="feeType"
                      value={editForm.feeType}
                      onChange={handleChange}
                      className="border p-1 w-full"
                    >
                      <option value="">--</option>
                      <option value="monthly">Monthly</option>
                      <option value="onetime">One-time</option>
                    </select>
                  </td>
                  <td className="border px-2 py-1">
                    <input
                      name="admissionFee"
                      value={editForm.admissionFee || ''}
                      onChange={handleChange}
                      className="border p-1 w-full"
                    />
                  </td>
                  <td className="border px-2 py-1">
                    <input
                      name="monthlyFee"
                      value={editForm.monthlyFee || ''}
                      onChange={handleChange}
                      className="border p-1 w-full"
                    />
                  </td>
                  <td className="border px-2 py-1">
                    <input
                      name="onetimeFee"
                      value={editForm.onetimeFee || ''}
                      onChange={handleChange}
                      className="border p-1 w-full"
                    />
                  </td>
                  <td className="border px-2 py-1">
                    <input
                      name="teacher"
                      value={editForm.teacher || ''}
                      onChange={handleChange}
                      className="border p-1 w-full"
                    />
                  </td>
                  <td className="border px-2 py-1">
                    <select
                      name="organization"
                      value={editForm.organization}
                      onChange={handleChange}
                      className="border p-1 w-full"
                    >
                      <option value="">--</option>
                      <option value="COG">COG</option>
                      <option value="Salankaraa">Salankaraa</option>
                    </select>
                  </td>
                  <td className="border px-2 py-1">
                    <select
                      name="mode"
                      value={editForm.mode || ''}
                      onChange={handleChange}
                      className="border p-1 w-full"
                    >
                      <option value="offline">Offline</option>
                      <option value="online">Online</option>
                    </select>
                  </td>
                  <td className="border px-2 py-1">
                    <select
                      name="status"
                      value={editForm.status || 'active'}
                      onChange={handleChange}
                      className="border p-1 w-full capitalize"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </td>
                  <td className="border px-2 py-1 space-x-2">
                    <button onClick={() => handleSave(course._id)} className="text-blue-600">Save</button>
                    <button onClick={handleCancelClick} className="text-gray-500">Cancel</button>
                  </td>
                </>
              ) : (
                <>
                  <td className="border px-2 py-1">{course.name}</td>
                  <td className="border px-2 py-1 capitalize">{course.feeType || '-'}</td>
                  <td className="border px-2 py-1">{formatCurrency(course.admissionFee)}</td>
                  <td className="border px-2 py-1">{formatCurrency(course.monthlyFee)}</td>
                  <td className="border px-2 py-1">{formatCurrency(course.onetimeFee)}</td>
                  <td className="border px-2 py-1">{course.teacher || '-'}</td>
                  <td className="border px-2 py-1">{course.organization || '-'}</td>
                  <td className="border px-2 py-1 capitalize">{course.mode || '-'}</td>
                  <td className="border px-2 py-1 capitalize">{course.status || '-'}</td>
                  <td className="border px-2 py-1 space-x-2">
                    <button
                      onClick={() => handleEditClick(course)}
                      className="text-blue-600"
                    >
                      Edit
                    </button>
                    {String(course?.status || '').toLowerCase() === 'inactive' ? (
                      <button
                        onClick={() => handleDelete(course._id)}
                        className="text-red-500"
                      >
                        Delete
                      </button>
                    ) : (
                      <span className="text-gray-400 italic ml-2">Delete (inactive only)</span>
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CourseList;